<script lang="ts">
  import { onMount } from 'svelte';
  import type { Source } from '../ws';
  import { loadLinkPreview, type LinkPreview } from '../linkPreview';
  import { sanitizeUrl } from '../sanitizeUrl';

  let {
    source,
    palette,
  } = $props<{
    source: Source;
    palette: {
      textPrimary: string;
      textSecondary: string;
      accent: string;
    };
  }>();

  let cardElement: HTMLAnchorElement;
  let preview = $state<LinkPreview | null>(null);
  let loading = $state(false);
  let requested = $state(false);
  let faviconFailed = $state(false);

  // Los sources históricos pueden traer URLs con residuos (`...html)//n`).
  let cleanUrl = $derived(sanitizeUrl(source.url) || source.url);
  let displayTitle = $derived(preview?.title || source.title || source.domain || cleanUrl);
  let displayDomain = $derived(
    preview?.site_name || preview?.domain || source.domain || cleanUrl
  );

  async function requestPreview() {
    if (requested) return;
    requested = true;
    loading = true;
    preview = await loadLinkPreview(cleanUrl);
    loading = false;
  }

  onMount(() => {
    if (!('IntersectionObserver' in window)) {
      void requestPreview();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          void requestPreview();
        }
      },
      { rootMargin: '160px' }
    );
    observer.observe(cardElement);
    return () => observer.disconnect();
  });
</script>

<a
  bind:this={cardElement}
  class="preview-card"
  href={cleanUrl}
  target="_blank"
  rel="noopener noreferrer"
  title={cleanUrl}
  aria-label={`Abrir fuente: ${displayTitle}`}
  aria-busy={loading}
>
  <div class="preview-copy">
    <div class="preview-origin" style="color: {palette.textSecondary};">
      <span
        class="favicon-shell"
        style={`background: color-mix(in srgb, ${palette.accent} 12%, transparent); color: ${palette.accent};`}
      >
        <i class="bi bi-globe2"></i>
        {#if preview?.favicon_url && !faviconFailed}
          <img
            src={preview.favicon_url}
            alt=""
            loading="lazy"
            referrerpolicy="no-referrer"
            onerror={() => (faviconFailed = true)}
          />
        {/if}
      </span>
      <span class="preview-domain">{displayDomain}</span>
      <i class="bi bi-box-arrow-up-right preview-open"></i>
    </div>

    <div class="preview-title" style="color: {palette.textPrimary};">
      {displayTitle}
    </div>

    {#if preview?.description}
      <div class="preview-description" style="color: {palette.textSecondary};">
        {preview.description}
      </div>
    {:else if loading}
      <div class="preview-skeleton" aria-hidden="true">
        <span></span>
        <span></span>
      </div>
    {/if}

  </div>
</a>

<style>
  .preview-card {
    display: flex;
    min-width: 0;
    min-height: 88px;
    overflow: hidden;
    border: 1px solid rgba(128, 128, 128, 0.16);
    border-radius: 10px;
    background: rgba(128, 128, 128, 0.045);
    color: inherit;
    text-decoration: none;
    transition: border-color 120ms ease, background-color 120ms ease, transform 120ms ease;
  }

  .preview-card:hover {
    border-color: rgba(128, 128, 128, 0.3);
    background: rgba(128, 128, 128, 0.08);
    transform: translateY(-1px);
  }

  .preview-card:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }

  .preview-copy {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 5px;
    padding: 10px;
  }

  .preview-title {
    display: -webkit-box;
    overflow: hidden;
    font-size: 12px;
    font-weight: 650;
    line-height: 1.3;
    letter-spacing: -0.005em;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }

  .preview-description {
    display: -webkit-box;
    overflow: hidden;
    font-size: 10.5px;
    line-height: 1.35;
    opacity: 0.86;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    line-clamp: 3;
  }

  .preview-origin {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 10px;
  }

  .favicon-shell {
    position: relative;
    width: 18px;
    height: 18px;
    display: grid;
    place-items: center;
    flex: 0 0 18px;
    overflow: hidden;
    border-radius: 5px;
    font-size: 10px;
  }

  .favicon-shell img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #fff;
  }

  .preview-domain {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .preview-open {
    flex-shrink: 0;
    font-size: 9px;
    opacity: 0.65;
  }

  .preview-skeleton {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .preview-skeleton span {
    height: 5px;
    border-radius: 999px;
    background: rgba(128, 128, 128, 0.13);
    animation: preview-pulse 1.2s ease-in-out infinite alternate;
  }

  .preview-skeleton span:last-child {
    width: 65%;
  }

  @keyframes preview-pulse {
    to { opacity: 0.42; }
  }

  @media (prefers-reduced-motion: reduce) {
    .preview-card { transition: none; }
    .preview-card:hover { transform: none; }
    .preview-skeleton span { animation: none; }
  }
</style>
